import type { PmsSkuStock, SmsCoupon } from '~/types/ProductDetails';
import type { PmsProduct } from '~/types';
import type { OmsCartItem } from '~/types/OmsCartItem';
import type { CartPromotionItem } from '~/types/ConfirmOrder';
export const useCartStore = defineStore('cart', () => {
    const cartItems = ref<CartPromotionItem[]>([])
    // const savedItems = ref<CartPromotionItem[]>([])
    const selectedItems = ref<Record<number, boolean>>({})
    const errorLoading = ref()
    const cartRepo = cartRepository()
    // const saveLaterStore = useSaveLater()
    const toast = useToast();

    function reset() {
        cartItems.value = []
        selectedItems.value = {}
        errorLoading.value = undefined

    }
    async function fetchCartList() {
        try {
            const { data, error, status } = await cartRepo.fetchCartList()
            if (data.value !== null && data.value.code === 200) {
                // cartItems.value = data.value.data
                errorLoading.value = undefined
            } else {
                console.error('Failed to fetch cart list:', error);
                errorLoading.value = 'Error during fetching cart list ' + status.value
            }
        } catch (reason) {
            errorLoading.value = 'Error during fetching cart list: ' + reason
            console.error('Error during fetching cart list:', reason);
        }
    }


    async function fetchCartListPromotions() {
        try {
            const data = await cartRepo.fetchCartListPromotions()
            if (data !== null && data.code === 200) {
                cartItems.value = data.data

                errorLoading.value = undefined
                selectedItems.value = Object.fromEntries(
                    cartItems.value.map(item => [item.id, true])
                );

            } else {
                console.error('Failed to fetch cart list:', data?.message);
                errorLoading.value = 'Error during fetching cart list ' + data.message
            }
        } catch (reason) {
            errorLoading.value = 'Error during fetching cart list: ' + reason
            console.error('Error during fetching cart list:', reason);
        }
    }

    async function addToCart(product: PmsProduct, skuStock?: PmsSkuStock) {
        try {
            const data = await cartRepo.addCartItem(convertToCartItem(product, skuStock))
            if (data !== null && data.code === 200) {
                return 'ok'
            } else {
                return 'Error during adding to cart ' + data.code
            }
        } catch (reason) {
            console.error('Error during adding to cart:', reason);
            return 'Error during adding to cart: ' + reason
        }
    }

    async function removeCartItem(ids: number[]) {
        try {
            const data = await cartRepo.deleteCartItem(ids)
            if (data !== null && data.code === 200) {
                for (let i = 0; i < ids.length; i++) {
                    const index = cartItems.value.findIndex(item => item.id === ids[i]);
                    if (index !== -1) {
                        cartItems.value.splice(index, 1);
                        toast.add({
                            title: 'Removed from cart',
                        })
                    }
                }
                return 'ok'
            } else {
                return 'Error during removing from cart ' + status
            }
        } catch (reason) {
            console.error('Error during removing from cart:', reason);
            return 'Error during removing from cart: ' + reason
        }
    }

    async function addToSaveLater(id: number) {
        try {
            const data = await cartRepo.saveLater(id)
            if (data.code === 200) {
                const index = cartItems.value.findIndex(item => item.id === id)
                if (index !== -1) {
                    // cartItems.value.splice(index, 1);
                    cartItems.value[index].isSavedItem = 1;

                    // Force reactivity by reassigning the array
                    // cartItems.value = [...cartItems.value];
                }
                toast.add({
                    title: 'Added to save later',
                })
                return 'ok'
            } else {
                return 'Error during adding to save later ' + data.message
            }
        } catch (reason) {
            console.error('Error during adding to save later:', reason);
            return 'Error during adding to save later: ' + reason
        }
    }


    async function moveToCart(id: number) {
        try {
            const data = await cartRepo.moveToCart(id)
            if (data.code === 200) {
                const index = cartItems.value.findIndex(item => item.id === id)
                if (index !== -1) {
                    cartItems.value[index].isSavedItem = 0;
                    toast.add({
                        title: 'Moved to cart',
                    })
                }
                return 'ok'
            } else {
                return 'Error during moving to cart ' + data.message
            }
        } catch (reason) {
            console.error('Error during moving to cart:', reason);
            return 'Error during moving to cart: ' + reason
        }
    }

    async function updateQuantity(cartId: number, quantity: number) {
        try {
            const data = await cartRepo.updateQuantity(cartId, quantity)
            if (data !== null && data.code === 200) {
                return 'ok'
            } else {
                return 'Error during updating cart ' + data.message
            }
        } catch (reason) {
            console.error('Error during updating cart:', reason);
            return 'Error during updating cart: ' + reason
        }
    }

    function calculateTotalPrice(): number {
        const totalPrice = filteredCartItems.value.reduce((sum, obj) => {
            if (obj.realStock == null || obj.realStock < 1) {
                return sum;
            } else {
                return sum + (obj.price ?? 0) * (obj.quantity ?? 0);
            }
        }, 0).toFixed(2);

        return Number(totalPrice);
    }

    function calculateTotalPriceDiscounted(): number {
        const totalPrice = filteredCartItems.value.reduce((sum, obj) => {
            return sum + (obj.reduceAmount ?? 0) * (obj.quantity ?? 0);
        }, 0).toFixed(2);
        return Number(totalPrice);
    }

    function calculateTotalAmountAfterDiscount(): number {
        const totalPrice = calculateTotalPrice();
        const totalPriceDiscounted = calculateTotalPriceDiscounted();
        let totalAmountAfterDiscount = totalPrice - totalPriceDiscounted;

        let couponAmount = 0;
        for (const element of filteredCartItems.value) {
            couponAmount += getMaxCoupon(element)?.amount ?? 0;
        }
        totalAmountAfterDiscount -= couponAmount;

        return totalAmountAfterDiscount
    }


    function getMaxCoupon(item: any): any | null {
        if (!item.coupons) return null;

        let maxAmountCoupon: any | null = null;
        let maxAmount = 0;

        for (const coupon of item.coupons) {
            const couponAmount = coupon.amount ?? 0;
            if (couponAmount > maxAmount) {
                maxAmount = couponAmount;
                maxAmountCoupon = coupon;
            }
        }

        return maxAmountCoupon;
    }

    const cartItemsComputed = computed(() => {
        return cartItems.value.filter(item => item.isSavedItem === 0);
    });

    function convertToCartItem(product?: PmsProduct, skuStock?: PmsSkuStock): OmsCartItem {
        if (skuStock === null || !skuStock) {
            return {
                productId: product?.id,
                price: product?.price ?? product?.promotionPrice,
                productSkuId: 0,
                quantity: 1,
                productName: product?.name,
                productSubTitle: product?.subTitle,
                productSn: product?.productSn,
                productBrand: product?.brandName,
                productPic: product?.pic,
                productCategoryId: product?.productCategoryId,
                isSavedItem: 0
            };
        } else {
            return {
                productId: product?.id,
                price: skuStock?.price,
                productSkuId: skuStock?.id ?? 0,
                productSkuCode: skuStock?.skuCode,
                quantity: 1,
                productName: product?.name,
                productSubTitle: product?.subTitle,
                productSn: product?.productSn,
                productBrand: product?.brandName,
                productPic: skuStock?.pic ?? product?.pic,
                productCategoryId: product?.productCategoryId,
                productAttr: skuStock?.combination,
                isSavedItem: 0
            };
        }
    }
    const filteredCartItems = computed(() => {
        return cartItemsComputed.value.filter(item => selectedItems.value[item.id ?? -1]);
    });

    const isItemSelected = computed(() => {
        return cartItemsComputed.value.some((item, index) => selectedItems.value[item.id ?? -1]);
    });


    function clearCartItems() {
        cartItems.value.filter;
    }
    const cartIds = computed(() => {
        return filteredCartItems.value
            .map(item => item.id)
            .filter((id): id is number => id !== undefined); // Filters out undefined values
    });


    function discountPercentage(cartItem: CartPromotionItem): number {
        const originalPrice = cartItem.price
        const promotionPrice = cartItem.reduceAmount;

        if (originalPrice && promotionPrice) {
            const diff = originalPrice - promotionPrice
            return Math.round((1 - diff / originalPrice) * 100)
        }

        return 0;
    }

    function priceAfterDiscount(cartItem: CartPromotionItem): number {
        return (cartItem.price ?? 0) - (cartItem.reduceAmount ?? 0);

    }

    function priceAfterCoupon(cartItem: CartPromotionItem): number {
        //  const couponIdAppliedAmount - maxAmountCoupon(cartItem).amount??0
        return priceAfterDiscount(cartItem) - (maxAmountCoupon(cartItem)?.amount ?? 0);
    }


    function maxAmountCoupon(cartItem: CartPromotionItem): SmsCoupon | undefined {
        return cartItem.coupons?.reduce((max, coupon) => ((coupon?.count ?? 0) > (max.amount ?? 0) ? coupon : max), cartItem.coupons[0] ?? null);
    }

    return {
        errorLoading,
        selectedItems,
        cartIds,
        addToCart,
        discountPercentage,
        priceAfterDiscount,
        maxAmountCoupon,
        priceAfterCoupon,
        cartItemsComputed,
        isItemSelected,
        fetchCartListPromotions,
        updateQuantity,
        removeCartItem,
        addToSaveLater,
        moveToCart,
        calculateTotalPrice,
        calculateTotalPriceDiscounted,
        calculateTotalAmountAfterDiscount,
        cartItems,
        reset
    }
}, {
    persist: {
        key: 'cart-store',
        storage: piniaPluginPersistedstate.localStorage(),
    },
});
